import { axiosInstance } from '@/libs/api-user-mgt/axios-instance';

import type {
  ActiveDelegations,
  CreateDelegationInput,
  DelegationCandidates,
  DelegationOperator,
  DelegationRecord,
  InboundDelegation,
  OutboundDelegation
} from '@/userManagement/domain/entities/Delegation';

type WireRecord = Record<string, unknown>;

function readString(o: WireRecord, camel: string, pascal: string): string {
  const v = o[camel] ?? o[pascal];
  return typeof v === 'string' ? v : '';
}

function readNullableString(o: WireRecord, camel: string, pascal: string): string | null {
  const v = o[camel] ?? o[pascal];
  if (v == null) return null;
  const s = String(v).trim();
  return s === '' ? null : s;
}

function readNullableIso(o: WireRecord, camel: string, pascal: string): string | null {
  const v = o[camel] ?? o[pascal];
  if (v == null) return null;
  return String(v);
}

function mapOutboundDelegation(raw: unknown): OutboundDelegation | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as WireRecord;
  const delegationId = readString(o, 'delegationId', 'DelegationId');
  if (delegationId === '') return null;
  return {
    delegationId,
    delegateUserId: readString(o, 'delegateUserId', 'DelegateUserId'),
    delegateDisplayName: readString(o, 'delegateDisplayName', 'DelegateDisplayName'),
    endsAt: readNullableIso(o, 'endsAt', 'EndsAt'),
    reason: readNullableString(o, 'reason', 'Reason')
  };
}

function mapInboundDelegation(raw: unknown): InboundDelegation | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as WireRecord;
  const delegationId = readString(o, 'delegationId', 'DelegationId');
  if (delegationId === '') return null;
  return {
    delegationId,
    delegatorUserId: readString(o, 'delegatorUserId', 'DelegatorUserId'),
    delegatorDisplayName: readString(o, 'delegatorDisplayName', 'DelegatorDisplayName'),
    delegatorRoleName: readString(o, 'delegatorRoleName', 'DelegatorRoleName'),
    endsAt: readNullableIso(o, 'endsAt', 'EndsAt')
  };
}

function mapActiveDelegations(raw: unknown): ActiveDelegations {
  const o = (raw ?? {}) as WireRecord;
  const outboundRaw = o.outboundDelegation ?? o.OutboundDelegation;
  const inboundRaw = o.inboundDelegations ?? o.InboundDelegations;
  const inboundList = Array.isArray(inboundRaw)
    ? inboundRaw.map(mapInboundDelegation).filter((d): d is InboundDelegation => d != null)
    : [];
  return {
    outboundDelegation: mapOutboundDelegation(outboundRaw),
    inboundDelegations: inboundList
  };
}

function mapDelegationOperator(raw: unknown): DelegationOperator | null {
  if (raw == null || typeof raw !== 'object') return null;
  const o = raw as WireRecord;
  const username = readString(o, 'username', 'Username');
  if (username === '') return null;
  const isEligibleRaw = o.isEligible ?? o.IsEligible;
  return {
    username,
    displayName: readString(o, 'displayName', 'DisplayName'),
    roleName: readString(o, 'roleName', 'RoleName'),
    isEligible: isEligibleRaw == null ? true : Boolean(isEligibleRaw),
    ineligibilityReason: readNullableString(o, 'ineligibilityReason', 'IneligibilityReason')
  };
}

function mapDelegationCandidates(raw: unknown): DelegationCandidates {
  const o = (raw ?? {}) as WireRecord;
  const delegatorsRaw = o.delegators ?? o.Delegators;
  const delegatesRaw = o.delegates ?? o.Delegates;
  const delegators = Array.isArray(delegatorsRaw)
    ? delegatorsRaw.map(mapDelegationOperator).filter((d): d is DelegationOperator => d != null)
    : [];
  const delegates = Array.isArray(delegatesRaw)
    ? delegatesRaw.map(mapDelegationOperator).filter((d): d is DelegationOperator => d != null)
    : [];
  return { delegators, delegates };
}

function mapDelegationRecord(raw: unknown): DelegationRecord {
  const o = (raw ?? {}) as WireRecord;
  return {
    id: readString(o, 'id', 'Id'),
    delegatorUserId: readString(o, 'delegatorUserId', 'DelegatorUserId'),
    delegateUserId: readString(o, 'delegateUserId', 'DelegateUserId'),
    delegatorDisplayName: readString(o, 'delegatorDisplayName', 'DelegatorDisplayName'),
    delegateDisplayName: readString(o, 'delegateDisplayName', 'DelegateDisplayName'),
    setUpByUserId: readNullableString(o, 'setUpByUserId', 'SetUpByUserId'),
    startsAt: readString(o, 'startsAt', 'StartsAt'),
    endsAt: readNullableIso(o, 'endsAt', 'EndsAt'),
    reason: readNullableString(o, 'reason', 'Reason'),
    isActive: Boolean(o.isActive ?? o.IsActive ?? true),
    revokedByUserId: readNullableString(o, 'revokedByUserId', 'RevokedByUserId'),
    revokedOn: readNullableIso(o, 'revokedOn', 'UtcRevokedOn'),
    createdOn: readString(o, 'createdOn', 'UtcCreatedOn')
  };
}

export const userManagementDelegationsSegment = {
  getActiveDelegations: async (): Promise<ActiveDelegations> => {
    const r = await axiosInstance<unknown>({
      url: '/api/user-management/v1/delegations/active',
      method: 'GET'
    });
    return mapActiveDelegations(r.data);
  },

  getDelegationCandidates: async (delegatorUserId?: string): Promise<DelegationCandidates> => {
    const r = await axiosInstance<unknown>({
      url: '/api/user-management/v1/delegations/candidates',
      method: 'GET',
      params: delegatorUserId?.trim() ? { delegatorUserId: delegatorUserId.trim() } : undefined
    });
    return mapDelegationCandidates(r.data);
  },

  createDelegation: async (input: CreateDelegationInput): Promise<DelegationRecord> => {
    const r = await axiosInstance<unknown>({
      url: '/api/user-management/v1/delegations',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: {
        delegatorUserId: input.delegatorUserId,
        delegateUserId: input.delegateUserId,
        endsAt: input.endsAt ?? null,
        reason: input.reason?.trim() ? input.reason.trim() : null
      }
    });
    return mapDelegationRecord(r.data);
  },

  revokeDelegation: async (delegationId: string): Promise<void> => {
    await axiosInstance<void>({
      url: `/api/user-management/v1/delegations/${delegationId}/revoke`,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      data: {}
    });
  }
};
